import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Calendar, Building2, FileText, ArrowRight } from 'lucide-react'
import DataResultsContainer from './DataResultsContainer.tsx'
import styles from './ConversationalMode.module.scss'
import {Info} from 'lucide-react'

interface MaterialQuotation {
  id: string | number
  name: string
  description?: string
  location?: string | { id?: number; name?: string }
  organism?: string | { id?: number; name?: string }
  workType?: string | { id?: number; name?: string }
  jurisdiccion?: { id?: number; name?: string }
  createdAt?: string
  endDate?: string
  isPublic?: boolean
  canQuote?: boolean
}

const getStringValue = (value: string | { id?: number; name?: string } | undefined): string | undefined => {
  if (!value) return undefined
  if (typeof value === 'string') return value
  return value.name || undefined
}

interface MaterialQuotationsListProps {
  quotations: MaterialQuotation[]
  total: number
  quotationType: string
  isPublic?: boolean
  noResultsMessage?: string
  openInNewWindow?: boolean
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'Sin fecha'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'Sin fecha'
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
}

const MaterialQuotationsList: React.FC<MaterialQuotationsListProps> = ({ 
  quotations, 
  total, 
  quotationType,
  isPublic = false,
  noResultsMessage,
  openInNewWindow = false
}) => {
  const navigate = useNavigate()
  const typeLabel = isPublic ? 'públicas' : 'privadas'
  const publicNote = isPublic 
    ? <><Info size={12} /> Las cotizaciones públicas son informativas. Para cotizar, usá el sistema de compras de cada jurisdicción.</> 
    : undefined

  return (
    <DataResultsContainer
      icon={<FileText size={18} />}
      title={`${quotations.length} cotización${quotations.length !== 1 ? 'es' : ''} ${typeLabel}`}
      subtitle={total > 3 ? `de ${total} disponibles` : undefined}
      color="orange"
      total={total}
      maxDisplay={3}
      viewAllLink={isPublic ? '/materiales-a-licitar-publicos' : '/cotizaciondemateriales'}
      viewAllText={`Ver las ${total} cotizaciones ${typeLabel}`}
      emptyMessage={noResultsMessage || `No se encontraron cotizaciones ${typeLabel} disponibles.`}
      emptyContactEmail="contacto@edify.la"
      note={publicNote}
      isEmpty={quotations.length === 0}
      openInNewWindow={openInNewWindow}
    >
      {quotations.slice(0, 3).map((quotation) => {
        const locationStr = getStringValue(quotation.location) || getStringValue(quotation.jurisdiccion)
        const organismStr = getStringValue(quotation.organism)
        const workTypeStr = getStringValue(quotation.workType)

        return (
          <div key={quotation.id} className={`${styles.resultCard} ${styles.resultCardOrange}`}>
            <div className={styles.resultCardHeader}>
              <span className={styles.resultCardTitle}>{quotation.name}</span>
              {isPublic && organismStr && (
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeOrange}`}>{organismStr}</span>
              )}
            </div>

            {quotation.description && (
              <p className={styles.resultCardDescription}>
                {quotation.description.length > 80 
                  ? `${quotation.description.substring(0, 80)}...` 
                  : quotation.description}
              </p>
            )}

            <div className={styles.resultCardMeta}>
              {locationStr && (
                <span><MapPin size={12} /> {locationStr}</span>
              )}
              {workTypeStr && (
                <span><Building2 size={12} /> {workTypeStr}</span>
              )}
              {quotation.endDate && (
                <span><Calendar size={12} /> Hasta {formatDate(quotation.endDate)}</span>
              )}
            </div>

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaOrange}`}
              onClick={() => {
                const url = isPublic 
                  ? `/materiales-a-licitar-publicos/${quotation.id}` 
                  : `/cotizaciondemateriales/${quotation.id}`
                if (openInNewWindow) {
                  window.open(`${window.location.origin}${url}`, '_blank')
                } else {
                  navigate(url)
                }
              }}
            >
              {isPublic ? 'Ver detalle' : 'Ver y cotizar'} <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
    </DataResultsContainer>
  )
}

export default MaterialQuotationsList
